/**
 * hooks/usePushNotifications.js
 *
 * Handles subscribing/unsubscribing from Web Push notifications.
 * Used by AdminLayout (type="admin") and DoctorLayout (type="doctor").
 *
 * Usage:
 *   const { supported, permission, subscribe, unsubscribe, subscribed } =
 *     usePushNotifications("admin");
 */

import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export default function usePushNotifications(ownerType = "admin") {
  const [supported,  setSupported]  = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading,    setLoading]    = useState(false);

  // Check initial state on mount
  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (ok) {
      setPermission(Notification.permission);
      // Check if already subscribed in this browser
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setSubscribed(!!sub);
        });
      }).catch(() => {});
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || loading) return;
    setLoading(true);
    try {
      // 1. Ask permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return;
      }

      // 2. Get VAPID public key from backend
      const { data } = await api.get("/push/vapid-public-key");
      if (!data?.publicKey) throw new Error("VAPID key not configured on server");

      // 3. Register service worker if not yet registered
      const reg = await navigator.serviceWorker.ready;

      // 4. Subscribe via PushManager
      const pushSub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      // 5. Send subscription to backend
      const subJson = pushSub.toJSON();
      await api.post(`/push/subscribe/${ownerType}`, {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys.p256dh,
          auth:   subJson.keys.auth,
        },
      });

      setSubscribed(true);
    } catch (err) {
      console.error("Push subscribe error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [supported, loading, ownerType]);

  const unsubscribe = useCallback(async () => {
    if (!supported || loading) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error("Push unsubscribe error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [supported, loading]);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
